/** Single application state with subscribe/notify and persisted UI preferences (SPEC «State»). */

import type { Lang } from './i18n';
import { normalizeCorrection } from './optics/availability';
import {
  isToric, type CorrectionChoice, type CorrectionId, type Profile, type Stage3, type Stage4, type Stage5,
} from './optics/profile';
import { toMinusCylinder } from './optics/refraction';
import { SCENE_IDS, type SceneId } from './scenes/types';

export interface State {
  lang: Lang;
  age: number;
  /** Refraction as an autorefractometer prints it (D-019): sphere, cylinder (either sign), axis. */
  sph: number;
  cyl: number;
  axis: number;
  /** No crystalline lens (D-021). Excludes cataract. */
  aphakia: boolean;
  cataract: Stage4;
  keratoconus: Stage4;
  glaucoma: Stage4;
  amd: Stage3;
  dr: Stage3;
  hr: Stage4;
  rp: Stage4;
  amblyopia: Stage5;
  leftCorrection: CorrectionId;
  rightCorrection: CorrectionId;
  monoTarget: 'far' | 'near';
  scene: SceneId;
  gaze: boolean;
  zoom: boolean;
  tags: boolean;
  /** Fixation point in render pixels, y-up (used by the retinal pass). */
  fixX: number;
  fixY: number;
}

/** Demo profile (SPEC §1): age 62, SPH −2.50 CYL −1.25 AXIS 180, cataract II; «after» image: toric trifocal. The left image is always without treatment (D-025). */
export const DEFAULTS: State = {
  lang: 'ru',
  age: 62,
  sph: -2.5,
  cyl: -1.25,
  axis: 180,
  aphakia: false,
  cataract: 2,
  keratoconus: 0,
  glaucoma: 0,
  amd: 0,
  dr: 0,
  hr: 0,
  rp: 0,
  amblyopia: 0,
  leftCorrection: 'none',
  rightCorrection: 'iol_tri_toric',
  monoTarget: 'far',
  scene: 'chart',
  gaze: false,
  zoom: false,
  tags: true,
  fixX: 576,
  fixY: 352,
};

const PREF_KEY = 'linza.prefs.v1';
const PREF_FIELDS = ['lang', 'scene'] as const;

function loadPrefs(): Partial<State> {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Partial<State> = {};
    if (parsed.lang === 'ru' || parsed.lang === 'uz' || parsed.lang === 'en') out.lang = parsed.lang;
    if (typeof parsed.scene === 'string' && (SCENE_IDS as string[]).includes(parsed.scene)) out.scene = parsed.scene as SceneId;
    return out;
  } catch {
    return {};
  }
}

function savePrefs(s: State): void {
  try {
    const prefs: Record<string, unknown> = {};
    for (const f of PREF_FIELDS) prefs[f] = s[f];
    localStorage.setItem(PREF_KEY, JSON.stringify(prefs));
  } catch {
    // Storage may be unavailable (private mode, embedded views). Preferences are a convenience only.
  }
}

export const state: State = { ...DEFAULTS, ...loadPrefs() };

export type Listener = (changed: Set<keyof State>) => void;
const listeners = new Set<Listener>();

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function set(k: keyof State, v: unknown, changed: Set<keyof State>): void {
  if (v === undefined || state[k] === v) return;
  (state as unknown as Record<string, unknown>)[k] = v;
  changed.add(k);
}

/** Apply a patch; notifies listeners with the keys whose values actually changed. */
export function update(patch: Partial<State>): void {
  const changed = new Set<keyof State>();
  for (const k of Object.keys(patch) as (keyof State)[]) set(k, patch[k], changed);
  if (changed.size === 0) return;

  // Consistency rules: aphakia excludes cataract; a selection that stopped being available falls back
  // to its non-toric version or to glasses (D-019, D-021).
  if (state.aphakia && state.cataract !== 0) set('cataract', 0, changed);
  const avail = { age: state.age, cyl: state.cyl, keratoconus: state.keratoconus, aphakia: state.aphakia };
  set('leftCorrection', normalizeCorrection(state.leftCorrection, avail), changed);
  set('rightCorrection', normalizeCorrection(state.rightCorrection, avail), changed);

  if (changed.has('lang') || changed.has('scene')) savePrefs(state);
  // Snapshot: a listener may unsubscribe and re-subscribe (UI rebuild on a language change), and a
  // Set visits members added during iteration, which would loop forever.
  for (const fn of [...listeners]) fn(changed);
}

export function profileOf(s: State): Profile {
  const r = toMinusCylinder({ sph: s.sph, cyl: s.cyl, axis: s.axis });
  return {
    age: s.age,
    sph: r.sph,
    cyl: r.cyl,
    axis: r.axis,
    aphakia: s.aphakia,
    cataract: s.aphakia ? 0 : s.cataract,
    keratoconus: s.keratoconus,
    glaucoma: s.glaucoma,
    amd: s.amd,
    dr: s.dr,
    hr: s.hr,
    rp: s.rp,
    amblyopia: s.amblyopia,
  };
}

export function choiceOf(s: State, side: 'left' | 'right'): CorrectionChoice {
  const id = side === 'left' ? s.leftCorrection : s.rightCorrection;
  return { id, toric: isToric(id), monoTarget: s.monoTarget };
}
